/**
 * Bascule de parc — étape 4 du chantier droits-multi-parcs (décision D6).
 *
 * Un seul module pour toute bascule (bouton du nom du parc, liste des parcs
 * AKONGO). Ce qui garde le parc d'avant au-delà d'un rechargement est vidé ici,
 * puis la page entière est rechargée sur l'accueil, et les autres onglets
 * ouverts rechargent à leur tour : aucun écran ne mélange deux parcs.
 *
 * Ce qui survit à un rechargement, et donc à vider :
 *   - la tranche `zoo` de l'état Redux persisté (`angle-store-key`) : parc, nom,
 *     rôle, niveau d'offre, filtres de secteur et de personne, fuseau, type ;
 *   - les clés lues à l'initialisation de cette tranche (`tempZooTier`,
 *     `zoneFilter`, `userFilter`) et les rôles mis en cache (`userRight`,
 *     `userRole`) ;
 *   - la copie locale du profil (`userData`), réécrite pour le nouveau parc par
 *     le connecteur, son seul point d'écriture.
 * Tout le reste (caches en mémoire, écouteurs) disparaît avec le rechargement.
 */
import { ACTIVE_PARK_KEY, writeStoredActivePark } from "./activePark";

export const REDUX_STORAGE_KEY = "angle-store-key";
export const PROFILE_STORAGE_KEY = "userData";
export const PARK_SCOPED_STORAGE_KEYS = Object.freeze([
  "tempZooTier", "zoneFilter", "userFilter", "userRight", "userRole",
  // Brouillon de création de campagne : il ne porte pas son parc et se
  // reprendrait dans un autre (animaux et personnes du parc précédent).
  "akongo:campaignCreation:draft",
]);

// Parc de CET onglet : la copie du profil est commune aux onglets, la mémoire de
// l'onglet non. Tenu par le seul point d'écriture de la copie (connecteur) et
// lu par la synchronisation entre onglets.
let tabParkId = null;
export function rememberTabPark(zooId) {
  tabParkId = typeof zooId === "string" && zooId ? zooId : null;
}
export function currentTabPark() {
  return tabParkId;
}

/** Parc porté par une copie du profil (texte JSON de localStorage), ou null. */
export function parkOfProfileSnapshot(raw) {
  try {
    const value = JSON.parse(raw || "null");
    return value && typeof value === "object" ? value.userZoo?.value || value.zooId || null : null;
  } catch (_error) {
    return null;
  }
}

/**
 * Tranche `zoo` de l'état Redux persisté, rechargée au démarrage : si un autre
 * onglet l'a réécrite avec un autre parc que la copie du profil (juste avant ou
 * après une bascule), la copie fait foi et la tranche repart de zéro.
 */
export function reconcileZooSliceWithProfile(zoo, profile) {
  const zooId = profile ? profile.userZoo?.value || profile.zooId || null : null;
  if (!zoo || typeof zoo !== "object" || !zooId || zoo.zooId === zooId) return zoo;
  const role = profile.userRight && typeof profile.userRight === "object" ? profile.userRight.value : profile.userRight;
  return {
    ...zoo,
    zooId,
    zooName: profile.userZoo?.label || "",
    zooTier: "essential",
    userRole: role || "user",
    zoneFilter: null,
    userFilter: null,
    timezone: null,
    zooType: null,
    zooOptions: {},
    productScopeReady: false,
    productScopeSelectionPending: false,
    productScopeSelectionRevision: 0,
  };
}

/** Tranche `zoo` persistée, vidée de tout ce qui vient du parc précédent. */
export function resetPersistedZooSlice(storage, { zooId, zooName = null }) {
  let state;
  try {
    state = JSON.parse(storage.getItem(REDUX_STORAGE_KEY) || "null");
  } catch (_error) {
    state = null;
  }
  if (!state || typeof state !== "object" || !state.zoo || typeof state.zoo !== "object") return;
  state.zoo = {
    ...state.zoo,
    zooId,
    zooName: zooName || "",
    zooTier: "essential",
    userRole: "user",
    zoneFilter: null,
    userFilter: null,
    timezone: null,
    zooType: null,
    zooOptions: {},
    productScopeReady: false,
    productScopeSelectionPending: false,
    productScopeSelectionRevision: 0,
  };
  storage.setItem(REDUX_STORAGE_KEY, JSON.stringify(state));
}

export function clearParkScopedStorage(storage) {
  for (const key of PARK_SCOPED_STORAGE_KEYS) storage.removeItem(key);
}

/**
 * Bascule vers `zooId`, sans le rechargement : le registre est relu d'abord ;
 * un parc non autorisé ne touche à rien. Sinon : parc mémorisé, copie du profil
 * réécrite pour ce parc par le connecteur, état du parc précédent vidé. À
 * l'appelant de recharger aussitôt (la liste des parcs affiche d'abord son
 * message).
 */
export async function prepareActivePark({ uid, zooId, zooName = null, connector, storage }) {
  if (!(await connector._canOpenPark(uid, zooId))) return { switched: false, reason: "PARK_NOT_ALLOWED" };
  writeStoredActivePark(storage, { uid, zooId, zooName });
  const result = await connector._getUserData(uid, true);
  if (result?.data?.zooId !== zooId) return { switched: false, reason: "PARK_NOT_OPENED" };
  clearParkScopedStorage(storage);
  resetPersistedZooSlice(storage, { zooId, zooName });
  return { switched: true, zooId };
}

/** Bascule vers `zooId` puis rechargement complet sur `homePath`. */
export async function switchActivePark({ uid, zooId, zooName = null, connector, storage, location, homePath }) {
  const result = await prepareActivePark({ uid, zooId, zooName, connector, storage });
  if (result.switched) location.replace(homePath);
  return result;
}

/**
 * Liste des parcs AKONGO : un membre du staff dont le registre n'a pas ce parc
 * doit d'abord s'y ajouter (commande journalisée, visible dans « Accès staff »
 * du parc). Sans registre (avant le rattrapage), il garde l'accès global : rien
 * à ajouter. Registre illisible : rien non plus, la bascule le refusera.
 */
export async function staffSelfAddNeeded({ uid, zooId, staff, connector }) {
  if (!staff) return false;
  const { grant, grantState } = await connector._getOwnAccessGrant(uid);
  return grantState === "present" && grant?.zoos?.[zooId]?.status !== "active";
}

/** Ouvre un parc depuis la liste : ajout de soi si besoin (`selfAdd`), puis bascule sans rechargement. */
export async function openParkFromList({ uid, zoo, selfAdd = false, addSelf, connector, storage }) {
  if (selfAdd) await addSelf({ uid, zooId: zoo.zooId });
  return prepareActivePark({ uid, zooId: zoo.zooId, zooName: zoo.zooName || null, connector, storage });
}

/**
 * Autres onglets : quand le parc actif ou la copie du profil change ailleurs
 * vers un autre parc, cet onglet recharge sur l'accueil plutôt que de continuer
 * à afficher l'ancien parc en écrivant dans le nouveau. Rend la fonction de
 * désinscription.
 */
export function installActiveParkTabSync({ target, getCurrentZooId = currentTabPark, location, homePath }) {
  const parkOf = (key, raw) => {
    if (key !== ACTIVE_PARK_KEY) return parkOfProfileSnapshot(raw);
    try {
      const value = JSON.parse(raw || "null");
      return value && typeof value === "object" ? value.zooId || null : null;
    } catch (_error) {
      return null;
    }
  };
  const onStorage = (event) => {
    if (event.key !== ACTIVE_PARK_KEY && event.key !== PROFILE_STORAGE_KEY) return;
    const next = parkOf(event.key, event.newValue);
    const current = getCurrentZooId();
    if (next && current && next !== current) location.replace(homePath);
  };
  target.addEventListener("storage", onStorage);
  return () => target.removeEventListener("storage", onStorage);
}
